"use client";

import { AutomatedContentBanner } from "@2u-landing-pages/components/AutomatedContent";
import { Button } from "@2u-landing-pages/components/Button";
import { ContentfulImage } from "@2u-landing-pages/components/ContentfulImage";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { ItemsContainer } from "@2u-landing-pages/components/ItemsContainer";
import { Banner } from "@2u-landing-pages/templates/Banner";
import {
  ContentfulImageConfig,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeItems_containerWithoutUnresolvableLinksResponse,
  TypeProgram_metaWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useProductCardHighlight } from "@2u-landing-pages/providers";
import { computeGalleryRecommendedFlag, isScrollHighlightedCard } from "@2u-landing-pages/utils/productCardHighlight";
import { camelCase, dispatchEduListingFromForm, generateId, getGradientOverlayStyle, useIntersectionObserver } from "@2u-landing-pages/utils";
import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useMetadataParser } from "@2u-landing-pages/utils/useMetadataParser";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { IconFeatherChevronDown, IconFeatherChevronUp } from "@red-digital/bricks";
import cn from "classnames";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { CustomCSSProperties, ProductCardProps } from "../../ProductCard.type";
// @ts-ignore
import styles from "./Default.module.scss";

export function trimProgramMetaName(str: string): string {
  const parts = str.trim().split("-");
  if (parts.length <= 2) return "";
  return parts.slice(1, -1).join("-").toLowerCase();
}

type AccordionItemProps = {
  content: any;
  parseMetadata: (template: string) => string;
  schoolProgramName: string;
};

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ content, parseMetadata, schoolProgramName }, ref) => {
    const { body, open } = content?.fields || {};
    const [isExpanded, setIsExpanded] = useState(Boolean(open));
    const contentRef = useRef<HTMLDivElement>(null);

    const handleOnClick = () => {
      setIsExpanded((expanded) => {
        const isExpanded = !expanded;
        EventClient.elementClicked({
          webElement: {
            elementType: "Landing Page",
            htmlId: window.location.href,
            location: schoolProgramName,
            name: isExpanded ? "OPEN" : "CLOSED",
            text: isExpanded ? "Show less" : "Show more",
          },
        });
        return isExpanded;
      });
    };

    useEffect(() => {
      if (ref && typeof ref !== "function") {
        ref.current = contentRef.current;
      }
    }, [ref]);

    return (
      <div key={content?.sys?.id} className={styles.accordion}>
        <div ref={contentRef} className={`${styles.accordionContent} ${isExpanded ? styles.expanded : ""}`}>
          <div className={styles.accordionContentInner}>
            {body?.content?.length > 0 && (
              <>
                {documentToReactComponents(body, {
                  renderMark,
                  renderText: (text) => renderText(parseMetadata(text)),
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (node, children) => <div>{children}</div>,
                    [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, "Accordion Item", body),
                  },
                })}
              </>
            )}
          </div>
        </div>
        <button className={styles.accordionToggle} onClick={handleOnClick}>
          {isExpanded ? "Show less" : "Show more"}
          {isExpanded ? <IconFeatherChevronUp /> : <IconFeatherChevronDown />}
        </button>
      </div>
    );
  }
);

AccordionItem.displayName = "AccordionItem";

export const Default = forwardRef<HTMLDivElement, ProductCardProps>((props, ref) => {
  const {
    product,
    index = 0,
    currentPage = 1,
    lob,
    category,
    position,
    location,
    mode,
    standalone,
    className,
    extended = false,
    disableRecommendedFlag = false,
  } = props;
  const inspector = useContentfulInspectorMode();
  const { highlightedCardId } = useProductCardHighlight();
  const accordionRef = useRef<HTMLDivElement>(null);
  const viewCorrelationId = useRef<string>(generateId());
  const hasBeenViewed = useRef<boolean>(false);
  const hasBeenLoaded = useRef<boolean>(false);

  const {
    program_meta,
    product_card_id,
    eyebrow,
    eyebrow_color,
    heading,
    heading_color,
    product_card_flag,
    product_card_flag_text_color,
    product_card_flag_background_color,
    banner_top,
    banner_bottom,
    product_card_content,
    product_card_buttons,
    school_photo,
    school_photo_config,
    show_dropshadow,
    show_school_photo_desktop,
    show_school_photo_mobile,
    form,
    product_card_size,
    buttons_background_color,
    show_school_logo = true,
    header_background_color,
  } = product?.fields || {};

  const { fields: programMeta, parseMetadata } = useMetadataParser(
    program_meta as TypeProgram_metaWithoutUnresolvableLinksResponse
  );

  const productEventData = {
    product: {
      productId: trimProgramMetaName(programMeta?.name) || "",
      name: programMeta?.topic || "",
      category: `mp-${lob}-${category}` || "",
      brand: programMeta?.school?.fields?.slug || "",
      variant: "",
      position,
    },
    viewCorrelationId: viewCorrelationId.current,
  };

  const photoConfig = school_photo_config as unknown as ContentfulImageConfig;
  const desktopCfg = photoConfig?.default;
  const mobileCfg = photoConfig?.mobile;

  const { main_content } = form?.fields || {};
  const { image } = main_content?.fields || {};

  const hasFlagContent = Boolean(product_card_flag?.content?.length);

  const isRecommended = computeGalleryRecommendedFlag({
    disableRecommendedFlag,
    extended,
    hasFlagContent,
    index,
    currentPage,
  });

  const isScrollHighlighted = isScrollHighlightedCard(
    highlightedCardId,
    product_card_id,
    product?.sys?.id,
  );

  const showRecommendedTreatment = isRecommended || isScrollHighlighted;
  const showRecommendedFlag = showRecommendedTreatment && hasFlagContent;
  const showSchoolPhoto = school_photo?.fields?.file?.url && !extended;

  const style: CustomCSSProperties = {};
  if (eyebrow_color) style["--eyebrow-color"] = `var(${eyebrow_color})`;
  if (heading_color) style["--heading-color"] = `var(${heading_color})`;
  if (show_school_photo_desktop) style["--show-school-photo-desktop"] = "block";
  if (show_school_photo_mobile) style["--show-school-photo-mobile"] = "block";
  if (product_card_flag_text_color) style["--product-card-flag-text-color"] = `var(${product_card_flag_text_color})`;
  if (product_card_flag_background_color)
    style["--product-card-flag-background-color"] = `var(${product_card_flag_background_color})`;
  if (buttons_background_color) style["--buttons-background-color"] = `var(${buttons_background_color})`;
  if (header_background_color) style["--header-background-color"] = `var(${header_background_color})`;

  if (mode === "multi-program") style["--buttons-background-color"] = undefined;

  if (desktopCfg?.focalPoint) style["--focal-point-desktop"] = desktopCfg.focalPoint;
  if (mobileCfg?.focalPoint) style["--focal-point-mobile"] = mobileCfg.focalPoint;

  const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
  const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};
  Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);

  const handleIntersect = useCallback(() => {
    if (hasBeenViewed.current) return;
    hasBeenViewed.current = true;

    EventClient.productViewed(productEventData);
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });
  const setObserver = useCallback(
    (element: HTMLElement | null) => {
      if (element) addObserver(element);
    },
    [addObserver]
  );

  useEffect(() => {
    if (hasBeenLoaded.current) return;
    hasBeenLoaded.current = true;

    EventClient.productLoaded(productEventData);
  }, []);

  const renderContent = (content: any) => {
    if (!content) return null;

    const contentType = content?.sys?.contentType?.sys?.id;
    const badge = content?.fields?.items?.some(
      (item: { fields?: { variation?: string } }) => item?.fields?.variation === "badge"
    );
    const grid = content?.fields?.desktop_layout_orientation === "grid" && !badge;
    const stacked = content?.fields?.desktop_layout_orientation === "stacked" && !badge;

    switch (contentType) {
      case "items_container":
        return (
          <ItemsContainer
            key={content?.sys?.id}
            className={cn(styles.itemsContainer, {
              [styles.itemsContainerGrid]: grid,
              [styles.itemsContainerStacked]: stacked,
              [styles.itemsContainerBadge]: badge,
            })}
            orientation="horizontal"
            entry={content as TypeItems_containerWithoutUnresolvableLinksResponse}
          />
        );
      case "accordion_item":
        return (
          <AccordionItem
            key={content?.sys?.id}
            content={content}
            parseMetadata={parseMetadata}
            ref={accordionRef}
            schoolProgramName={`${heading} - ${eyebrow}`}
          />
        );
      case "content_full":
        return (
          <ContentLayout
            key={content?.sys?.id}
            content={content as TypeContent_fullWithoutUnresolvableLinksResponse}
          />
        );
      default:
        return null;
    }
  };

  const renderBanner = (content: any, position: "top" | "bottom") => {
    const contentType = content?.sys?.contentType?.sys?.id;
    const isTopBanner = position === "top";
    const location = `Product Card ${isTopBanner ? "Top" : "Bottom"} Banner`;
    switch (contentType) {
      case "automated_content":
        return (
          <div className={isTopBanner ? styles.bannerTop : styles.bannerBottom}>
            <AutomatedContentBanner {...content.fields} metaStartDate={programMeta?.start_date} location={location} />
          </div>
        );
      case "banner":
        return (
          <div className={isTopBanner ? styles.bannerTop : styles.bannerBottom}>
            <Banner
              {...content.fields}
              className={cn(isTopBanner ? "banner-top" : "banner-bottom", styles.banner)}
              inspector={inspector}
              location={location}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const combinedRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }

      if (element) {
        setObserver(element);
        (element as any).accordionRef = accordionRef;
      }
    },
    [ref, accordionRef]
  );

  if (!product?.fields) {
    return null;
  }

  return (
    <div
      ref={combinedRef}
      id={product_card_id || `product-card-${product?.sys?.id || ""}`}
      data-name={parseMetadata(`${programMeta?.school_name} - ${programMeta?.program_name}`)}
      className={cn(
        className,
        styles.productCard,
        product_card_size && standalone ? styles[camelCase(`card-size-${product_card_size}`)] : "",
        {
          [styles.recommended]: showRecommendedTreatment,
          [styles.dropshadow]: show_dropshadow || extended,
          [styles.cardWithBanner]: banner_top,
          [styles.standalone]: standalone,
          [styles.extended]: extended,
        }
      )}
      style={style}
    >
      {showRecommendedFlag && (
        <div className={styles.recommendedFlag} style={style}>
          {product_card_flag && (
            <>
              {documentToReactComponents(product_card_flag, {
                renderMark,
                renderText: (text) => renderText(parseMetadata(text)),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => <div>{children}</div>,
                  [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, "Product Card Flag", product_card_flag),
                },
              })}
            </>
          )}
        </div>
      )}
      <div className={styles.card}>
        {banner_top && renderBanner(banner_top, "top")}

        <aside
          data-eq-section="featured"
          className={cn(styles.featuredSection, {
            [styles.showSchoolLogo]: show_school_logo,
            [styles.showSchoolPhoto]: showSchoolPhoto && show_school_photo_desktop,
          })}
        >
          <div className={styles.featuredContent}>
            {programMeta?.logo && show_school_logo && (
              <ContentfulImage image={programMeta?.logo} className={styles.logo} width={200} loading="lazy" />
            )}
            <div className={styles.heading}>
              {eyebrow && <p className={styles.subtitle}>{parseMetadata(eyebrow)}</p>}
              {heading && <h2 className={styles.title}>{parseMetadata(heading)}</h2>}
            </div>
          </div>
          {showSchoolPhoto && (
            <div
              className={cn(styles.featuredImage, {
                [styles.featuredImageDesktop]: show_school_photo_desktop,
                [styles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
                [styles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
              })}
            >
              <ContentfulImage
                key={`school-photo-${product?.sys?.id || ""}`}
                image={school_photo}
                config={photoConfig}
                loading="eager"
                {...inspector({
                  entryId: product?.sys?.id || "",
                  fieldId: "school_photo",
                })}
              />
            </div>
          )}
        </aside>

        <div className={styles.content}>
          <div className={styles.heading}>
            {eyebrow && <p className={styles.subtitle}>{parseMetadata(eyebrow)}</p>}
            {heading && <h2 className={styles.title}>{parseMetadata(heading)}</h2>}
          </div>
          {(product_card_content || [])?.map((content, idx) => (
            <div key={content?.sys?.id || `content-${idx}`} data-eq-section={`content-${idx}`}>
              {renderContent(content)}
            </div>
          ))}
        </div>

        {banner_bottom && renderBanner(banner_bottom, "bottom")}

        <div data-eq-section="cta" className={styles.ctaSection}>
          {(product_card_buttons || [])?.length > 0 && (
            <div className={styles.buttonWrapper}>
              {product_card_buttons?.map((button, index) => {
                const { link_url, ...buttonProps } = button?.fields || {};
                const action = button?.fields?.action;
                // OPEN_LINK has custom handling below for correlation/tracking, so avoid
                // passing link_url to the shared Button to prevent duplicate navigation.
                // Other actions (OPEN_MODAL, SLIDE_TO_ELEMENT, OPEN_CALENDLY, etc.)
                // depend on link_url in Button's internal action handler.
                const buttonLinkUrl = action === "OPEN_LINK" ? undefined : link_url;

                return (
                  <Button
                    key={`${button?.sys?.id}-${index}`}
                    location={`${heading} - ${eyebrow}`}
                    {...(button?.fields?.action === "OPEN_DRAWER" && { drawerType: "multi-program" })}
                    {...buttonProps}
                    link_url={buttonLinkUrl}
                    onClick={(e) => {
                      e.stopPropagation();
                      const correlationId = generateId();
                      const clickEventData = {
                        ...productEventData,
                        correlationId,
                      };

                      EventClient.productClicked(clickEventData);

                      if (button?.fields?.action === "OPEN_DRAWER" && form) {
                        try {
                          dispatchEduListingFromForm(form, {
                            imageUrl: image?.fields?.file?.url || "",
                          });
                        } catch (error) {
                          console.error("Error creating drawer event data:", error);
                        }
                      } else if (button?.fields?.action === "OPEN_LINK") {
                        const destinationUrl = button?.fields?.link_url;
                        const btnName = button?.fields?.name || "";
                        if (!destinationUrl) return;

                        EventClient.elementClicked({
                          webElement: {
                            elementType: "Product Card CTA Redirect",
                            htmlId: window.location.href,
                            location: `${location ? `${location} - ` : ""}Product Card`,
                            position: "CTA",
                            name: `${btnName ? `${btnName} - ` : ""}${correlationId}`,
                            text: parseMetadata(button?.fields?.label || ""),
                          },
                        });

                        const correlationIdParam =
                          (program_meta?.fields?.school?.fields?.correlation_id_param || "").trim() || "utm_subId1";

                        try {
                          const url = new URL(destinationUrl);
                          new URLSearchParams(window.location.search).forEach((value, key) => {
                            url.searchParams.set(key, value);
                          });

                          url.searchParams.set(
                            correlationIdParam,
                            correlationId
                          );

                          window.open(url.toString(), "_blank");
                        } catch (error) {
                          const urlParams = new URLSearchParams(window.location.search);
                          urlParams.set(correlationIdParam, correlationId);
                          const redirectUrl = `${destinationUrl}?${urlParams.toString()}`;
                          window.open(redirectUrl, "_blank");
                        }
                      }
                    }}
                  >
                    {parseMetadata(button?.fields?.label || "")}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

Default.displayName = "ProductCard";
